
// MapboxTmapRoute.jsx
import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
//import "./MapboxTmapRoute.css"; // (선택) 여기에 map container sizing 등 CSS를 넣어두면 편함

// === 설정: 본인의 키로 바꿔주세요 ===
mapboxgl.accessToken = "pk.eyJ1IjoiaGFuamluc3UiLCJhIjoiY20wZXJ3NWJyMDI3ZDJrczRxZXpwcG0yZyJ9.g953AvZzd8Om0aAoIxH5HA";
const TMAP_APPKEY = "RXrar1aAI05Cbx7fXw7Ih3sfkuhlDYBTiRq81NAd"; // 예시 값 — 본인 키로 변경

// 교통 타입 → 색상 매핑 (원활/서행/지체/정체)
const TRAFFIC_COLOR = {
  1: "#00B344", // 원활
  2: "#0063EA", // 서행
  3: "#FF952B", // 지체
  4: "#E4151D", // 정체
  default: "#808080",
};

export default function MapboxTmapRoute() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // UI / 상태
  const [startAddr, setStartAddr] = useState("");
  const [endAddr, setEndAddr] = useState("");
  const [requirements, setRequirements] = useState("");
  const [activeInput, setActiveInput] = useState("start");
  const [searchResults, setSearchResults] = useState([]);
  const [routeSteps, setRouteSteps] = useState([]);

  // Mapbox 마커 보관
  const markersRef = useRef([]);

  // 음성 인식 (선택)
  const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // --------------------------------------------
  // 1) Mapbox 초기화 (3D 빌딩 포함)
  // --------------------------------------------
  useEffect(() => {
    if (mapRef.current) return; // 이미 초기화되었으면 종료

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [126.978, 37.5665],
      zoom: 13,
      pitch: 60,
      bearing: -10,
      antialias: true,
    });

    map.on("load", () => {
      // 3D buildings (Mapbox 기본 소스 'composite'에 빌딩 레이어가 있는 경우)
      if (!map.getLayer("3d-buildings")) {
        map.addLayer(
          {
            id: "3d-buildings",
            source: "composite",
            "source-layer": "building",
            type: "fill-extrusion",
            minzoom: 15,
            paint: {
              "fill-extrusion-color": "#aaa",
              "fill-extrusion-height": [
                "interpolate",
                ["linear"],
                ["zoom"],
                15,
                0,
                15.05,
                ["get", "height"],
              ],
              "fill-extrusion-base": ["get", "min_height"],
              "fill-extrusion-opacity": 0.6,
            },
          },
          "waterway-label"
        );
      }

      // 초기 빈 route source/layer
      if (!map.getSource("tmap-route")) {
        map.addSource("tmap-route", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        map.addLayer({
          id: "tmap-route-line",
          type: "line",
          source: "tmap-route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": ["coalesce", ["get", "color"], "#0063EA"],
            "line-width": 10
          }
        });
        
      }

      setMapLoaded(true);
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // --------------------------------------------
  // 유틸: 마커 정리 / 추가
  // --------------------------------------------
  const clearMapMarkers = () => {
    (markersRef.current || []).forEach((m) => {
      try {
        m.remove();
      } catch (e) {}
    });
    markersRef.current = [];
  };

  const addMapMarker = (lng, lat, opts = {}) => {
    if (!mapRef.current) return;
    const el = document.createElement("div");
    el.className = "tmap-marker";
    if (opts.iconUrl) {
      el.style.backgroundImage = `url(${opts.iconUrl})`;
      el.style.width = opts.width || "28px";
      el.style.height = opts.height || "34px";
      el.style.backgroundSize = "contain";
    } else {
      el.style.width = "14px";
      el.style.height = "14px";
      el.style.borderRadius = "50%";
      el.style.background = opts.color || "#1E90FF";
      el.style.border = "2px solid white";
    }

    const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
      .setLngLat([lng, lat])
      .addTo(mapRef.current);

    if (opts.popupHTML) {
      const popup = new mapboxgl.Popup({ offset: 12 }).setHTML(opts.popupHTML);
      marker.setPopup(popup);
    }

    markersRef.current.push(marker);
    return marker;
  };

  // --------------------------------------------
  // 2) POI 검색(자동완성) - Tmap POI API
  //    - Tmap이 JSON을 바로 반환하는 경우와 result(...) 래핑된 경우 둘 다 처리
  // --------------------------------------------
  const safeParseMaybeWrapped = async (res) => {
    const text = await res.text();
    // wrap 형태: result({...});
    if (/^result\(/.test(text)) {
      try {
        const jsonStr = text.replace(/^result\(/, "").replace(/\);?$/, "");
        return JSON.parse(jsonStr);
      } catch (e) {
        console.error("wrapped parse failed", e);
      }
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      // fallback to res.json()
      try {
        return await res.json();
      } catch (err) {
        console.error("parse failed", err);
        return null;
      }
    }
  };

  const searchAddress = async (keyword) => {
    if (!keyword || keyword.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    try {
      const url = `https://apis.openapi.sk.com/tmap/pois?version=1&format=json&searchKeyword=${encodeURIComponent(
        keyword
      )}&count=7&resCoordType=WGS84GEO&reqCoordType=WGS84GEO&appKey=${TMAP_APPKEY}`;

      const res = await fetch(url, { headers: { appKey: TMAP_APPKEY } });
      const data = await safeParseMaybeWrapped(res);
      const pois = data?.searchPoiInfo?.pois?.poi || [];
      setSearchResults(pois);
    } catch (err) {
      console.error("POI search error:", err);
      setSearchResults([]);
    }
  };

  // --------------------------------------------
  // 3) 지오코딩 (주소 -> 좌표)
  // --------------------------------------------
  const geocodeAddress = async (address) => {
    const url = `https://apis.openapi.sk.com/tmap/geo/fullAddrGeo?version=1&format=json&appKey=${TMAP_APPKEY}&coordType=WGS84GEO&fullAddr=${encodeURIComponent(
      address
    )}`;

    const res = await fetch(url);
    const data = await res.json();
    const coord = data?.coordinateInfo?.coordinate?.[0];
    if (!coord) throw new Error("주소에서 좌표를 찾을 수 없습니다.");
    const lat = Number(coord.lat || coord.newLat);
    const lon = Number(coord.lon || coord.newLon);
    return { lat, lon };
  };

  // --------------------------------------------
  // 4) POI 선택 -> 입력 채우기 + 마커
  // --------------------------------------------
  const handleSelectPoi = async (poi) => {
    // POI의 주소 텍스트 만들기 (안정적으로)
    const address =
      poi?.newAddressList?.[0]?.fullAddress ||
      [poi.upperAddrName, poi.middleAddrName, poi.lowerAddrName].filter(Boolean).join(" ") ||
      poi.name;

    if (activeInput === "start") setStartAddr(address);
    else if (activeInput === "end") setEndAddr(address);
    else setRequirements(address);

    setSearchResults([]);

    try {
      const { lat, lon } = await geocodeAddress(address);
      clearMapMarkers();
      addMapMarker(lon, lat, { color: "#1E90FF", popupHTML: `<strong>${poi.name}</strong><div>${address}</div>` });
      mapRef.current.easeTo({ center: [lon, lat], zoom: 16 });
    } catch (err) {
      console.error("poi select geocode error", err);
    }
  };

  // --------------------------------------------
  // 5) 길찾기 호출 (Tmap routes)
  // --------------------------------------------
  const handleFindRoute = async () => {
    if (!startAddr || !endAddr) {
      alert("출발지와 도착지를 입력하세요.");
      return;
    }
    try {
      // 지오코딩
      const start = await geocodeAddress(startAddr);
      const end = await geocodeAddress(endAddr);

      // Tmap routes 호출 (POST)
      const url = "https://apis.openapi.sk.com/tmap/routes?version=1&format=json";
      const body = {
        startX: String(start.lon),
        startY: String(start.lat),
        endX: String(end.lon),
        endY: String(end.lat),
        reqCoordType: "WGS84GEO",
        resCoordType: "WGS84GEO",
        searchOption: 0,
        trafficInfo: "Y",
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          appKey: TMAP_APPKEY,
          "x-app-key": TMAP_APPKEY, // 일부 API는 x-app-key 필요
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!data || !data.features) throw new Error("길찾기 결과 없음");

      // Mapbox에 그릴 GeoJSON 생성
      const features = [];

      data.features.forEach((f) => {
        if (!f.geometry) return;

        // LineString: 교통 정보가 들어있으면 segment별로 잘라서 color 속성 추가
        if (f.geometry.type === "LineString") {
          const coords = f.geometry.coordinates; // [ [lon, lat], ... ]
          const traffic = f.geometry.traffic;

          if (Array.isArray(traffic) && traffic.length > 0) {
            // traffic entries like [startIdx, endIdx, trafficType]
            traffic.forEach(([sIdx, eIdx, tType]) => {
              const segCoords = coords.slice(sIdx, eIdx + 1).map(([lon, lat]) => [lon, lat]);
              features.push({
                type: "Feature",
                properties: { color: TRAFFIC_COLOR[tType] || TRAFFIC_COLOR.default, type: "line" },
                geometry: { type: "LineString", coordinates: segCoords },
              });
            });
          } else {
            // no traffic -> single line
            features.push({
              type: "Feature",
              properties: { color: "#0063EA", type: "line" },
              geometry: { type: "LineString", coordinates: coords },
            });
          }
        }

        // Point: 예: 경유 포인트/안내(졸음쉼터 등)
        if (f.geometry.type === "Point" && f.properties) {
          const [lon, lat] = f.geometry.coordinates;
          // show restarea marker for turnType 150 (예)
          if (f.properties.turnType === 150) {
            features.push({
              type: "Feature",
              properties: { type: "rest", name: f.properties.name || "휴게소" },
              geometry: { type: "Point", coordinates: [lon, lat] },
            });
          } else {
            // other points (turn instructions) - we don't draw them as separate features here
          }
        }
      });

      // 출발/도착 마커
      clearMapMarkers();
      addMapMarker(start.lon, start.lat, { color: "#00b300", popupHTML: `<strong>출발</strong><div>${startAddr}</div>` });
      addMapMarker(end.lon, end.lat, { color: "#e60000", popupHTML: `<strong>도착</strong><div>${endAddr}</div>` });

      // route steps (Point features with descriptions)
      const steps = data.features
        .filter((f) => f.geometry && f.geometry.type === "Point" && f.properties && f.properties.description)
        .map((f) => ({ turnType: f.properties.turnType, description: f.properties.description }));
      setRouteSteps(steps);

      // set GeoJSON to source
      const geojson = { type: "FeatureCollection", features };
      const map = mapRef.current;

      if (map.getSource("tmap-route")) {
        map.getSource("tmap-route").setData(geojson);
      } else {
        map.addSource("tmap-route", { type: "geojson", data: geojson });
        map.addLayer({
          id: "tmap-route-line",
          type: "line",
          source: "tmap-route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: [
            "case",
            ["has", "color"],
            ["get", "color"],
            "#0063EA",
          ],
        });
      }

      // Add separate markers for Point-type features (e.g. rest)
      features
        .filter((ft) => ft.geometry.type === "Point")
        .forEach((pt) => {
          const [lon, lat] = pt.geometry.coordinates;
          addMapMarker(lon, lat, { color: "#ff9900", popupHTML: `<strong>${pt.properties.name || "POI"}</strong>` });
        });

      // 카메라 이동
      map.flyTo({ center: [start.lon, start.lat], zoom: 14, pitch: 60, duration: 800 });
    } catch (err) {
      console.error("route error:", err);
      alert("경로 탐색 중 오류가 발생했습니다: " + (err.message || err));
    }
  };

  // --------------------------------------------
  // 6) 음성 입력 (간단)
  // --------------------------------------------
  const startVoiceInput = (target) => {
    if (!SpeechRecognition) {
      alert("브라우저가 음성 입력을 지원하지 않습니다.");
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = "ko-KR";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.start();

    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      if (target === "start") setStartAddr(text);
      else if (target === "end") setEndAddr(text);
      else setRequirements(text);
    };

    rec.onerror = (ev) => {
      console.error("voice error", ev);
      alert("음성 인식 오류가 발생했습니다.");
    };
  };

  // --------------------------------------------
  // UI 렌더링
  // --------------------------------------------
  return (
    <div style={{ display: "flex", gap: 12 }}>
      {/* 지도 */}
      <div
        ref={mapContainerRef}
        style={{ width: "100%", height: "100vh", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.12)" }}
      />

      {/* 사이드바 */}
      {/* 사이드바 */}
<div
  style={{
    position: "absolute",
    backgroundColor: "white",
    padding: "16px",
    borderRadius: "20px",
    width: "320px",
    bottom: "20px",
    left: "20px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    
    /* 반응형 적용 */
    ...(window.innerWidth < 600
      ? {
          width: "100%",
          left: 0,
          bottom: 0,
          borderRadius: "20px 20px 0 0",
          maxHeight: "50vh",
          padding: "20px",
        }
      : {}),
  }}
>
  <h2 style={{ marginTop: 0, fontSize: window.innerWidth < 600 ? "18px" : "20px" }}>
    길찾기
  </h2>
<div>
  <div>
  <label style={{ fontSize: window.innerWidth < 600 ? "14px" : "16px" }}>출발지</label>
  </div>
  <input
    value={startAddr}
    onFocus={() => setActiveInput("start")}
    onChange={(e) => {
      setStartAddr(e.target.value);
      searchAddress(e.target.value);
    }}
    style={{
      width: "94%",
      marginBottom: 8,
      marginTop: 3,
      padding: "10px",
      fontSize: window.innerWidth < 600 ? "14px" : "15px",
      borderRadius: 6,
      border: "1px solid #ccc",
    }}
    placeholder="출발지 입력"
  />
</div>
  
 
  <div >
    <div>
    <label style={{ fontSize: window.innerWidth < 600 ? "14px" : "16px" }}>도착지</label>
    </div>
  
  <input
    value={endAddr}
    onFocus={() => setActiveInput("end")}
    onChange={(e) => {
      setEndAddr(e.target.value);
      searchAddress(e.target.value);
    }}
    style={{
      width: "94%",
      marginBottom: 8,
      marginTop: 3,
      padding: "10px",
      fontSize: window.innerWidth < 600 ? "14px" : "15px",
      borderRadius: 6,
      border: "1px solid #ccc",
    }}
    placeholder="도착지 입력"
  />

  </div>
  

  <label style={{ fontSize: window.innerWidth < 600 ? "14px" : "16px" }}>추가요청</label>
  <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
    <input
      value={requirements}
      onFocus={() => setActiveInput("requirements")}
      onChange={(e) => setRequirements(e.target.value)}
      style={{
        flex: 1,
        marginTop: 3,
        padding: 10,
        borderRadius: 6,
        border: "1px solid #ccc",
        fontSize: window.innerWidth < 600 ? "14px" : "15px",
      }}
      placeholder="요청/메모"
    />
    <button
      onClick={() => startVoiceInput("requirements")}
      style={{
        padding: "10px",
        fontSize: "18px",
        background: "#eee",
        borderRadius: 6,
        border: "1px solid #ccc",
      }}
    >
      🎤
    </button>
  </div>

  <button
    onClick={handleFindRoute}
    style={{
      width: "100%",
      padding: "12px",
      background: "#1E2027",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      fontSize: window.innerWidth < 600 ? "14px" : "16px",
      marginBottom: 10,
    }}
  >
    🚗 경로 찾기
  </button>

  {/* 자동완성 결과 */}
  {searchResults.length > 0 && (
    <div
      style={{
        marginTop: 12,
        maxHeight: 180,
        overflowY: "auto",
        border: "1px solid #eee",
        borderRadius: 6,
      }}
    >
      {searchResults.map((poi, idx) => (
        <div
          key={idx}
          onClick={() => handleSelectPoi(poi)}
          style={{
            padding: 10,
            borderBottom: "1px solid #f2f2f2",
            cursor: "pointer",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15 }}>{poi.name}</div>
          <div style={{ fontSize: 12, color: "#444" }}>
            {poi.newAddressList?.[0]?.fullAddress ??
              [poi.upperAddrName, poi.middleAddrName, poi.lowerAddrName]
                .filter(Boolean)
                .join(" ")}
          </div>
        </div>
      ))}
    </div>
  )}

  {/* 경로 안내 */}
  {routeSteps.length > 0 && (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ fontSize: window.innerWidth < 600 ? "14px" : "16px" }}>경로 안내</h4>
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {routeSteps.map((s, i) => (
          <div key={i} style={{ padding: 10, borderBottom: "1px solid #f6f6f6" }}>
            <div style={{ fontWeight: 700 }}>{s.turnType}</div>
            <div style={{ color: "#333" }}>{s.description}</div>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

    </div>
  );
}

